import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FileText, IdCard, Search, Users } from "lucide-react";
import {
  ADMIN_USER_ROUTES,
  KYC_DOCUMENT_KIND_LABELS,
  KYC_STATUS_LABELS,
  requiresKyc,
  ROLES,
  ROLE_LABELS,
  adminUserListResponseSchema,
  adminUserResponseSchema,
  type AdminUserList,
  type AdminUsersQuery,
  type KycStatus,
  type PublicUser,
  type Role,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Badge, Button, Input, Select, Textarea, type BadgeVariant } from "@/shared/ui/core";
import { ConfirmDialog, EmptyState, Modal, SkeletonTable } from "@/shared/ui/ui";
import { useAuth } from "@/features/auth/auth.context";
import {
  AdminGuard,
  DataTable,
  DataTableFrame,
  FilterBar,
  Pagination,
  SendReminderButton,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { formatOrderDate } from "@/features/admin/orders/format";
import { getKycForAdmin, reviewKyc } from "@/features/kyc/kyc.api";

const KYC_VARIANTS: Record<KycStatus, BadgeVariant> = {
  not_started: "neutral",
  pending_review: "warning",
  approved: "success",
  rejected: "error",
};


const COUNTRY_LABELS: Record<string, string> = {
  MX: "México",
  US: "Estados Unidos",
  OTHER: "Otro",
};

function listUsers(query: AdminUsersQuery): Promise<AdminUserList> {
  const params = new URLSearchParams();
  params.set("page", String(query.page));
  params.set("perPage", String(query.perPage));
  if (query.query) params.set("query", query.query);
  if (query.role) params.set("role", query.role);
  return authFetch(`${ADMIN_USER_ROUTES.list}?${params.toString()}`, adminUserListResponseSchema, {
    baseUrl: "",
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Usuarios — réplica de `admin/registered-users` (Anahí, 27-ago).
 * El censo de cuentas, buscable por nombre o correo. Solo lectura:
 * las acciones sobre cuentas llegarán como actos explícitos.
 *
 * La condición (cliente → socio → franquiciataria) se retiró de la
 * tabla el 28-ago: se deduce del KYC y del panel de cada quien, y como
 * columna solo robaba el ancho que necesitaban el nombre y el correo.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminUsersPage() {
  return (
    <AdminGuard>
      <UsersScreen />
    </AdminGuard>
  );
}

function UsersScreen() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o 20,
   * con CINCO por defecto. Vive en el estado y no como constante porque
   * ahora cambia en caliente; al cambiarlo se vuelve a la página 1, que es
   * lo único sensato — la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);

  const query: AdminUsersQuery = {
    page,
    perPage: perPage,
    ...(search.trim() ? { query: search.trim() } : {}),
    ...(roleFilter ? { role: roleFilter as Role } : {}),
  };
  const users = useQuery({
    queryKey: ["admin", "users", query],
    queryFn: () => listUsers(query),
    placeholderData: (previous) => previous,
  });

  const items = users.data?.items ?? [];
  const total = users.data?.total ?? 0;
  const totalAll = users.data?.totalAll ?? 0;
  const kycStatuses = users.data?.kycStatuses ?? {};
  /** El usuario cuyo expediente de KYC está abierto en el modal. */
  const [kycUser, setKycUser] = useState<PublicUser | null>(null);

  /*
   * ── El cambio de ROL (Anahí, 27-ago) ───────────────────────────────
   * El selector de la columna propone; el ConfirmDialog pregunta; el
   * servidor decide (nadie se cambia a sí mismo, el último super admin
   * no se degrada). Mover un rol mueve permisos: por eso confirma.
   */
  const { user: sessionUser } = useAuth();
  const queryClient = useQueryClient();
  const [pendingRole, setPendingRole] = useState<{ user: PublicUser; role: Role } | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);

  const cambiarRol = useMutation({
    mutationFn: async ({ user, role }: { user: PublicUser; role: Role }) => {
      const data = await authFetch(ADMIN_USER_ROUTES.role(user.id), adminUserResponseSchema, {
        baseUrl: "",
        method: "PATCH",
        body: { role },
      });
      return data.user;
    },
    onSuccess: () => {
      setPendingRole(null);
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (cause) => {
      setPendingRole(null);
      setRoleError(cause instanceof Error ? cause.message : t("No se pudo cambiar el rol"));
    },
  });

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Usuarios")}
        description={
          users.data
            ? total === totalAll
              ? t("{{count}} cuentas registradas, con su condición en el negocio.", { count: totalAll })
              : t("{{shown}} de {{count}} cuentas registradas coinciden con el filtro.", { shown: total, count: totalAll })
            : t("Todas las cuentas registradas, con su condición en el negocio.")
        }
      />

      <DataTableFrame aria-busy={users.isFetching}>
        {/* ── Los filtros dentro de la card: el MISMO patrón que Órdenes. */}
        <FilterBar>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Buscar")}
            <div className="relative w-64">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-ink-3" />
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Nombre o correo…")}
                aria-label={t("Buscar usuarios")}
                className="pl-10"
              />
            </div>
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Rol")}
            <div className="w-44">
              <Select
                aria-label={t("Rol")}
                value={roleFilter}
                onChange={(event) => {
                  setRoleFilter(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Todos")}
                options={ROLES.map((value) => ({ value, label: t(ROLE_LABELS[value]) }))}
              />
            </div>
          </label>
        </FilterBar>
        {users.isPending ? (
          <div className="p-5">
            <SkeletonTable rows={6} columns={6} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<Users className="h-5 w-5 text-ds-ink-3" />}
              title={search ? t("Sin resultados") : t("Todavía no hay cuentas")}
              description={
                search
                  ? t("Nadie coincide con esa búsqueda.")
                  : t("Cuando alguien se registre, aparece aquí.")
              }
            />
          </div>
        ) : (
          <DataTable caption={t("Usuarios registrados")}>
            <thead>
              <tr>
                {/*
                  Anchos repartidos a mano (Anahí, 28-ago: "que se
                  distribuyan bien"): con `auto`, el navegador le daba a
                  "Correo verificado" y a "KYC" el mismo peso que al
                  nombre, y los nombres largos se partían en tres
                  renglones mientras sobraba aire a la derecha.
                */}
                <Th className="w-[22%]">{t("Persona")}</Th>
                <Th className="w-[16%]">{t("Rol")}</Th>
                <Th className="w-[9%]">{t("País")}</Th>
                <Th className="w-[11%] whitespace-nowrap">{t("Verificado")}</Th>
                <Th className="w-[18%]">{t("KYC")}</Th>
                <Th align="center" className="w-[8%] whitespace-nowrap">{t("Recordar")}</Th>
                <Th align="right" className="w-[8%] whitespace-nowrap">{t("Registro")}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((user) => {
                return (
                  <Tr key={user.id}>
                    <Td>
                      <p className="font-medium">{user.fullName}</p>
                      {/*
                        El correo se RECORTA en vez de ensanchar la
                        columna (Anahí, 28-ago: "que cubra menos
                        espacio"): completo sigue en el `title` y en el
                        expediente de la persona.
                      */}
                      <p className="truncate text-[12px] text-ds-ink-3" title={user.email}>
                        {user.email}
                      </p>
                    </Td>
                    <Td>
                      {/* Tu propia fila no se toca: se lee, no se elige. */}
                      {user.id === sessionUser?.id ? (
                        <span className="text-[13px] text-ds-ink-3">
                          {t(ROLE_LABELS[user.role])}
                        </span>
                      ) : (
                        <div className="w-40">
                          <Select
                            aria-label={t("Rol de {{name}}", { name: user.fullName })}
                            value={user.role}
                            onChange={(event) =>
                              setPendingRole({ user, role: event.target.value as Role })
                            }
                            options={ROLES.map((value) => ({
                              value,
                              label: t(ROLE_LABELS[value]),
                            }))}
                          />
                        </div>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">
                      {COUNTRY_LABELS[user.country] ?? user.country}
                    </Td>
                    <Td>
                      <Badge variant={user.emailVerified ? "success" : "warning"} size="sm" dot>
                        {user.emailVerified ? t("Sí") : t("Pendiente")}
                      </Badge>
                    </Td>
                    <Td className="whitespace-nowrap">
                      {(() => {
                        // Estados Unidos no sube documentos (22-oct-2026).
                        if (!requiresKyc(user.country)) {
                          return (
                            <Badge variant="neutral" size="sm">
                              {t("No aplica")}
                            </Badge>
                          );
                        }
                        const status = (kycStatuses[user.id] ?? "not_started") as KycStatus;
                        return (
                          <button
                            type="button"
                            onClick={() => setKycUser(user)}
                            aria-label={t("Revisar el KYC de {{name}}", { name: user.fullName })}
                            className="group inline-flex items-center gap-1.5"
                          >
                            <Badge variant={KYC_VARIANTS[status]} size="sm" dot>
                              {t(KYC_STATUS_LABELS[status])}
                            </Badge>
                            <IdCard className="h-4 w-4 text-ds-ink-3 transition group-hover:text-ds-violet-pop" />
                          </button>
                        );
                      })()}
                    </Td>
                    <Td align="center">
                      {/*
                        El recordatorio de KYC va JUNTO al estado, que es
                        donde se ve que falta (Anahí, 28-ago). Solo
                        aparece si el expediente no está aprobado: a quien
                        ya cumplió no se le recuerda nada — y el servidor
                        rechaza el intento igual.
                      */}
                      {requiresKyc(user.country) &&
                        ((kycStatuses[user.id] ?? "not_started") as KycStatus) !== "approved" && (
                        <SendReminderButton userId={user.id} kind="kyc" iconOnly />
                      )}
                    </Td>
                    <Td align="right" className="whitespace-nowrap text-ds-ink-3">
                      {formatOrderDate(user.createdAt)}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </DataTable>
        )}

        {total > 0 && (
          <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        )}
      </DataTableFrame>

      {roleError && (
        <Alert variant="error" className="mt-4" onDismiss={() => setRoleError(null)}>
          {roleError}
        </Alert>
      )}

      <ConfirmDialog
        open={pendingRole !== null}
        title={t("Cambiar el rol")}
        description={
          pendingRole
            ? t('{{name}} pasará a ser "{{role}}". Los permisos cambian de inmediato.', {
                name: pendingRole.user.fullName,
                role: t(ROLE_LABELS[pendingRole.role]),
              })
            : ""
        }
        confirmLabel={t("Cambiar rol")}
        cancelLabel={t("Cancelar")}
        loading={cambiarRol.isPending}
        onConfirm={() => {
          if (pendingRole) cambiarRol.mutate(pendingRole);
        }}
        onCancel={() => setPendingRole(null)}
      />

      <KycReviewModal user={kycUser} onClose={() => setKycUser(null)} />
    </>
  );
}

/**
 * El expediente de una persona: sus documentos (imagen a la vista, PDF
 * con su enlace) y el veredicto — Aprobar, o Rechazar con la nota
 * OBLIGATORIA que dice qué corregir.
 */
function KycReviewModal({
  user,
  onClose,
}: {
  user: PublicUser | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const kyc = useQuery({
    queryKey: ["admin", "kyc", user?.id],
    queryFn: () => getKycForAdmin(user?.id ?? ""),
    enabled: user !== null,
  });

  const veredicto = useMutation({
    mutationFn: (status: "approved" | "rejected") =>
      reviewKyc(user?.id ?? "", {
        status,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      setNote("");
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "kyc", user?.id] });
      onClose();
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo guardar el veredicto")),
  });

  function decidir(status: "approved" | "rejected") {
    setError(null);
    if (status === "rejected" && !note.trim()) {
      setError(t("Para rechazar, escribe qué debe corregir."));
      return;
    }
    veredicto.mutate(status);
  }

  const expediente = kyc.data;

  return (
    <Modal
      open={user !== null}
      onClose={onClose}
      title={user ? t("KYC de {{name}}", { name: user.fullName }) : ""}
      size="lg"
    >
      {kyc.isPending ? (
        <p className="py-6 text-center text-[13px] text-ds-ink-3">{t("Abriendo el expediente…")}</p>
      ) : !expediente || expediente.documents.length === 0 ? (
        <div className="py-2">
          <EmptyState
            icon={<FileText className="h-5 w-5 text-ds-ink-3" />}
            title={t("Sin documentos")}
            description={t("Esta cuenta todavía no sube nada que revisar.")}
          />
        </div>
      ) : (
        <div className="grid gap-4">
          <div className="flex items-center gap-2">
            <Badge variant={KYC_VARIANTS[expediente.status]} size="sm" dot>
              {t(KYC_STATUS_LABELS[expediente.status])}
            </Badge>
            {expediente.reviewNote && (
              <p className="text-[12px] text-ds-ink-3">
                {t("Nota anterior: {{nota}}", { nota: expediente.reviewNote })}
              </p>
            )}
          </div>

          {/* ── Los documentos, a la vista ────────────────────────────── */}
          <div className="grid gap-3 sm:grid-cols-2">
            {expediente.documents.map((doc) => (
              <figure
                key={doc.kind}
                className="overflow-hidden rounded-2xl border border-ds-border bg-ds-cream-2/40"
              >
                {doc.dataUrl && doc.mimeType !== "application/pdf" ? (
                  <img
                    src={doc.dataUrl}
                    alt={t(KYC_DOCUMENT_KIND_LABELS[doc.kind])}
                    className="max-h-56 w-full object-contain bg-white"
                  />
                ) : (
                  <a
                    href={doc.dataUrl}
                    download={doc.fileName}
                    className="flex h-32 items-center justify-center gap-2 text-[13px] font-medium text-ds-violet-pop hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    {t("Abrir PDF")}
                  </a>
                )}
                <figcaption className="border-t border-ds-border px-3 py-2 text-[12px] text-ds-ink-3">
                  <span className="font-medium text-ds-ink">
                    {t(KYC_DOCUMENT_KIND_LABELS[doc.kind])}
                  </span>{" "}
                  · {doc.fileName} ·{" "}
                  {t("subido el {{fecha}}", { fecha: formatOrderDate(doc.uploadedAt) })}
                </figcaption>
              </figure>
            ))}
          </div>

          <Textarea
            label={t("Nota (obligatoria al rechazar)")}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            placeholder={t("Qué debe corregir, o un visto bueno")}
          />

          {error && (
            <Alert variant="error" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="secondary"
              loading={veredicto.isPending && veredicto.variables === "rejected"}
              onClick={() => decidir("rejected")}
            >
              {t("Rechazar")}
            </Button>
            <Button
              loading={veredicto.isPending && veredicto.variables === "approved"}
              onClick={() => decidir("approved")}
            >
              {t("Aprobar")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
